import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Btn, Spinner } from '../../components/ui';
import { adminAPI } from '../../api';

const BLANK = {
  email: '', first_name: '', last_name: '', title: 'Ms.', role: '',
  school_id: '', subject: '', grade_low: '', grade_high: '',
  assignment_note: '', password: '',
};

/** Roles whose accounts work across schools rather than belonging to one. */
const CROSS_SCHOOL = ['consultant', 'district', 'administrator'];

export default function UsersTab({ options, onChanged }) {
  const [users, setUsers]     = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editing, setEditing] = useState(null);   // user object, or BLANK for new
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminAPI.users({ ...(roleFilter && { role: roleFilter }), ...(query && { q: query }) }),
      adminAPI.schools(),
    ])
      .then(([u, s]) => { setUsers(u.data); setSchools(s.data); })
      .catch(err => { console.error(err); toast.error('Could not load accounts.'); })
      .finally(() => setLoading(false));
  }, [roleFilter, query]);

  useEffect(() => {
    const t = setTimeout(load, query ? 350 : 0);   // debounce typing
    return () => clearTimeout(t);
  }, [load, query]);

  const roleCodeOf = (roleId) =>
    options?.roles.find(r => r.id === roleId)?.code || '';

  const save = async () => {
    const body = { ...editing };
    const code = roleCodeOf(body.role);

    if (!body.email || !body.first_name || !body.last_name || !body.role) {
      toast.error('Email, first name, last name and role are all required.');
      return;
    }
    // A cross-school account has no single school to belong to.
    if (CROSS_SCHOOL.includes(code)) body.school_id = null;
    if (!body.password) delete body.password;

    setSaving(true);
    try {
      if (body.id) await adminAPI.updateUser(body.id, body);
      else         await adminAPI.createUser(body);
      toast.success(body.id ? 'Account updated.' : 'Account created.');
      setEditing(null);
      load();
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data;
      toast.error(
        (detail && Object.entries(detail).map(([k, v]) => `${k}: ${[].concat(v).join(' ')}`).join(' · '))
        || 'Could not save that account.'
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await adminAPI.updateUser(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Account deactivated.' : 'Account reactivated.');
      load();
      onChanged?.();
    } catch {
      toast.error('Could not change that account.');
    }
  };

  const editingCode = editing ? roleCodeOf(editing.role) : '';
  const needsSchool = editing && !CROSS_SCHOOL.includes(editingCode);

  return (
    <>
      <div className="adm-toolbar">
        <input className="fi" placeholder="Search name or email…"
               value={query} onChange={e => setQuery(e.target.value)} />
        <select className="fi" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          {options?.roles.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
      </div>

      <Btn variant="blue" onClick={() => setEditing({ ...BLANK })} style={{ marginBottom: 14 }}>
        Add staff member
      </Btn>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}><Spinner /></div>
      ) : users.length === 0 ? (
        <div className="dash-empty">No accounts match.</div>
      ) : users.map(u => (
        <div key={u.id} className={`adm-user${u.is_active ? '' : ' off'}`}>
          <div className="adm-user-main">
            <div className="adm-user-name">
              {u.display_name}
              {!u.is_active && <span className="adm-badge off">Deactivated</span>}
              {u.is_active && !u.last_login && <span className="adm-badge">Never signed in</span>}
            </div>
            <div className="adm-user-sub">{u.email}</div>
            <div className="adm-user-meta">
              {[u.role_name, u.school_name, u.assignment].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div className="adm-user-actions">
            <button className="adm-link" onClick={() => setEditing({ ...u, password: '' })}>Edit</button>
            <button className="adm-link" onClick={() => toggleActive(u)}>
              {u.is_active ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        </div>
      ))}

      {/* ── Add / edit ─────────────────────────────────────────────────── */}
      {editing && (
        <div className="sheet-backdrop" onClick={() => setEditing(null)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">{editing.id ? 'Edit account' : 'Add staff member'}</div>
              <button className="sheet-close" onClick={() => setEditing(null)}>✕</button>
            </div>

            <div className="sheet-body">
              <div className="adm-field-row">
                <div style={{ width: 90 }}>
                  <div className="sheet-lbl">Title</div>
                  <select className="fi" value={editing.title || ''}
                          onChange={e => setEditing({ ...editing, title: e.target.value })}>
                    <option value="">—</option>
                    {options?.titles.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="sheet-lbl">First name</div>
                  <input className="fi" value={editing.first_name}
                         onChange={e => setEditing({ ...editing, first_name: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="sheet-lbl">Last name</div>
                  <input className="fi" value={editing.last_name}
                         onChange={e => setEditing({ ...editing, last_name: e.target.value })} />
                </div>
              </div>

              <div className="sheet-lbl">Email</div>
              <input className="fi" type="email" value={editing.email}
                     onChange={e => setEditing({ ...editing, email: e.target.value })} />

              <div className="sheet-lbl">Role</div>
              <select className="fi" value={editing.role || ''}
                      onChange={e => setEditing({ ...editing, role: e.target.value })}>
                <option value="">Select role…</option>
                {options?.roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>

              {needsSchool && (
                <>
                  <div className="sheet-lbl">School</div>
                  <select className="fi" value={editing.school_id || ''}
                          onChange={e => setEditing({ ...editing, school_id: e.target.value })}>
                    <option value="">Select school…</option>
                    {schools.map(s => <option key={s.id} value={s.id}>{s.dropdown_label}</option>)}
                  </select>
                </>
              )}

              {editingCode === 'teacher' && (
                <>
                  <div className="sheet-lbl">Subject <span className="adm-hint">blank means all subjects</span></div>
                  <select className="fi" value={editing.subject || ''}
                          onChange={e => setEditing({ ...editing, subject: e.target.value })}>
                    <option value="">All subjects</option>
                    {options?.subjects.map(s => <option key={s}>{s}</option>)}
                  </select>

                  <div className="adm-field-row">
                    <div style={{ flex: 1 }}>
                      <div className="sheet-lbl">From grade</div>
                      <select className="fi" value={editing.grade_low || ''}
                              onChange={e => setEditing({ ...editing, grade_low: e.target.value })}>
                        <option value="">All grades</option>
                        {options?.grades.map(g => <option key={g}>{g}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="sheet-lbl">To grade</div>
                      <select className="fi" value={editing.grade_high || ''}
                              onChange={e => setEditing({ ...editing, grade_high: e.target.value })}>
                        <option value="">All grades</option>
                        {options?.grades.map(g => <option key={g}>{g}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="sheet-lbl">
                    Assignment note <span className="adm-hint">e.g. Science specialist</span>
                  </div>
                  <input className="fi" value={editing.assignment_note || ''}
                         onChange={e => setEditing({ ...editing, assignment_note: e.target.value })} />
                </>
              )}

              <div className="sheet-lbl">
                {editing.id ? 'New password' : 'Password'}
                <span className="adm-hint">
                  {editing.id ? 'leave blank to keep the current one' : 'at least 8 characters'}
                </span>
              </div>
              <input className="fi" type="password" value={editing.password || ''}
                     onChange={e => setEditing({ ...editing, password: e.target.value })} />
            </div>

            <div className="sheet-foot">
              <Btn variant="dark" onClick={save} loading={saving}>
                {editing.id ? 'Save changes' : 'Create account'}
              </Btn>
              <button className="sheet-cancel" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
